import React, { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import Switcher from "../../../components/switcher";

import { usePackages } from "../../../hooks/usePackages";
import RatingStars from "../../../components/RatingStars";

import {FiMapPin, FiChevronLeft, FiChevronRight} from '../../../assets/icons/vander'

const slugify = (str = '') => str.normalize('NFD').replace(/[\u0300-\u036f]/g,'').toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/(^-|-$)/g,'');

export default function TourGrid(){
    const { country } = useParams();
    const { packages, loading, error, refetch } = usePackages();

    const filtered = useMemo(()=>{
        if(!country) return packages;
        const target = country.toLowerCase();
        return packages.filter((p)=>{
            const city = slugify(p.destination?.city || '');
            const region = slugify(p.destination?.region || '');
            const countrySlug = slugify(p.destination?.country || '');
            return city === target || region === target || countrySlug === target;
        });
    },[country, packages]);
    const title = country ? `Tours en ${country.replace(/-/g,' ')}` : 'Paquetes turísticos';

    const [searchParams, setSearchParams] = useSearchParams();
    const perPage = 9;
    const list = filtered.length ? filtered : packages;
    const totalPages = Math.max(1, Math.ceil(list.length / perPage));
    const requestedPage = parseInt(searchParams.get('page') || '1', 10);
    const currentPage = Math.min(Math.max(1, Number.isNaN(requestedPage) ? 1 : requestedPage), totalPages);
    const pagedItems = list.slice((currentPage - 1) * perPage, currentPage * perPage);

    const goToPage = (page) => {
        if (page < 1 || page > totalPages || page === currentPage) return;
        const params = new URLSearchParams(searchParams);
        if (page === 1) {
            params.delete('page');
        } else {
            params.set('page', String(page));
        }
        setSearchParams(params);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const formatPrice = useMemo(
        () => (priceCents, currency = "USD") => {
            if (priceCents == null) return "";
            const amount = currency === "CLP" ? priceCents : priceCents / 100;
            return new Intl.NumberFormat("es-CL", {
                style: "currency",
                currency,
                minimumFractionDigits: currency === "CLP" ? 0 : 2,
            }).format(amount);
        },
        []
    );

    return(
        <>
        <Navbar navclass="defaultscroll is-sticky" navlight={true} manuclass="justify-end nav-light"/>

        <section className="relative table w-full items-center py-36 bg-[url('../images/bg/cta.jpg')] bg-top bg-no-repeat bg-cover">
            <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/80 to-slate-900"></div>
            <div className="container relative">
                <div className="grid grid-cols-1 pb-8 text-center mt-10">
                    <h1 className="text-4xl leading-normal tracking-wider font-semibold text-white [text-wrap:balance]">{title}</h1>
                </div>
            </div>
            
            <div className="absolute text-center z-10 bottom-5 start-0 end-0 mx-3">
                <ul className="tracking-[0.5px] mb-0 inline-block">
                    <li className="inline-block uppercase text-[13px] font-bold duration-500 ease-in-out text-white/50 hover:text-white"><Link to="/">Chile-Turismo.cl</Link></li>
                    <li className="inline-block text-base text-white/50 mx-0.5 ltr:rotate-0 rtl:rotate-180"><i className="mdi mdi-chevron-right"></i></li>
                    <li className="inline-block uppercase text-[13px] font-bold duration-500 ease-in-out text-white" aria-current="page">Tour</li>
                </ul>
            </div>
        </section>

        <section className="relative md:py-24 py-16">
            <div className="container relative">
                <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-6">
                    {loading && <p className="text-center text-slate-400 w-full">Cargando paquetes…</p>}
                    {error && (
                        <div className="text-center w-full">
                            <p className="text-red-500">{error}</p>
                            <button type="button" onClick={refetch} className="mt-3 py-2 px-5 inline-block bg-red-500 hover:bg-red-600 text-white rounded-md">Reintentar</button>
                        </div>
                    )}
                    {!loading && !error && pagedItems.map((item,index)=>{
                        const cover = item.images?.[0]?.url || item.hero_image_url || '';
                        return(
                            <div className="group rounded-md shadow dark:shadow-gray-700" key={index}>
                                <div className="relative overflow-hidden rounded-t-md shadow dark:shadow-gray-700 mx-3 mt-3">
                                    {cover ? (
                                        <img src={cover} width="400" height="240" loading="lazy" className="scale-125 group-hover:scale-100 duration-500 w-full h-[240px] object-cover" alt={item.title}/>
                                    ) : (
                                        <div className="h-[240px] bg-slate-200 dark:bg-slate-800 flex items-center justify-center text-slate-500">Sin imagen</div>
                                    )}

                                    <div className="absolute top-0 end-0 p-4">
                                        <button type="button" aria-label="Guardar en favoritos" className="size-8 inline-flex justify-center items-center bg-white dark:bg-slate-900 shadow dark:shadow-gray-800 rounded-full text-slate-100 dark:text-slate-700 focus:text-red-500 dark:focus:text-red-500 hover:text-red-500 dark:hover:text-red-500"><i className="mdi mdi-heart text-[20px] align-middle"></i></button>
                                    </div>
                                </div>

                                <div className="p-4">
                                    <p className="flex items-center text-slate-400 font-medium mb-2"><FiMapPin className="text-red-500 size-4 me-1"></FiMapPin> {item.place || item.destination?.name}</p>
                                    <Link to={`/tour-detail-one/${item.id}`} className="text-lg font-medium hover:text-red-500 duration-500 ease-in-out">{item.title}</Link>

                                    <RatingStars avg={item.rating_avg} count={item.rating_count} />

                                    <div className="mt-4 pt-4 flex justify-between items-center border-t border-slate-100 dark:border-gray-800">
                                        <h5 className="text-lg font-medium text-red-500">{formatPrice(item.price_cents, item.currency)}</h5>

                                        <Link to={`/tour-detail-one/${item.id}`} className="text-slate-400 hover:text-red-500">Explorar <i className="mdi mdi-arrow-right"></i></Link>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>

                {!loading && !error && totalPages > 1 && (
                <div className="grid md:grid-cols-12 grid-cols-1 mt-6">
                    <div className="md:col-span-12 text-center">
                        <nav aria-label="Paginación">
                            <ul className="inline-flex items-center -space-x-px">
                                <li>
                                    <button type="button" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} aria-label="Página anterior" className="size-[40px] inline-flex justify-center items-center text-slate-400 bg-white dark:bg-slate-900 rounded-s-3xl hover:text-white border border-gray-100 dark:border-gray-800 hover:border-red-500 dark:hover:border-red-500 hover:bg-red-500 dark:hover:bg-red-500 disabled:opacity-50 disabled:pointer-events-none">
                                        <FiChevronLeft className="size-5 rtl:rotate-180 rtl:-mt-1"></FiChevronLeft>
                                    </button>
                                </li>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                    <li key={page}>
                                        <button
                                            type="button"
                                            onClick={() => goToPage(page)}
                                            aria-label={`Página ${page}`}
                                            aria-current={page === currentPage ? "page" : undefined}
                                            className={page === currentPage
                                                ? "z-10 size-[40px] inline-flex justify-center items-center text-white bg-red-500 border border-red-500"
                                                : "size-[40px] inline-flex justify-center items-center text-slate-400 hover:text-white bg-white dark:bg-slate-900 border border-gray-100 dark:border-gray-800 hover:border-red-500 dark:hover:border-red-500 hover:bg-red-500 dark:hover:bg-red-500"}
                                        >{page}</button>
                                    </li>
                                ))}
                                <li>
                                    <button type="button" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} aria-label="Página siguiente" className="size-[40px] inline-flex justify-center items-center text-slate-400 bg-white dark:bg-slate-900 rounded-e-3xl hover:text-white border border-gray-100 dark:border-gray-800 hover:border-red-500 dark:hover:border-red-500 hover:bg-red-500 dark:hover:bg-red-500 disabled:opacity-50 disabled:pointer-events-none">
                                        <FiChevronRight className="size-5 rtl:rotate-180 rtl:-mt-1"></FiChevronRight>
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </div>
                )}
            </div>
        </section>
        <Footer/>
        <Switcher/>
        </>
    )
}
